import {Dialog, Tab} from '@headlessui/react';
import {useState} from "react";
import {API} from "../../api/config";

const TabButton = ({ selected, children }) => (
    <button
        className={`px-4 py-2 rounded-t-lg ${
            selected
                ? 'bg-white border-t border-x border-gray-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        }`}
    >
        {children}
    </button>
);

export const QuickCreateModal = ({ isOpen, onClose, onCreated }) => {
    return (
        <Dialog open={isOpen} onClose={onClose} className="relative z-50">
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
            <div className="fixed inset-0 flex items-center justify-center p-4">
                <Dialog.Panel className="w-[600px] bg-white rounded-xl shadow-lg">
                    <Tab.Group>
                        <div className="border-b border-gray-200">
                            <Tab.List className="flex gap-2 px-6 pt-4">
                                <Tab className="focus:outline-none">
                                    {({ selected }) => (
                                        <TabButton selected={selected}>Account</TabButton>
                                    )}
                                </Tab>
                                <Tab className="focus:outline-none">
                                    {({ selected }) => (
                                        <TabButton selected={selected}>Vendor</TabButton>
                                    )}
                                </Tab>
                            </Tab.List>
                        </div>

                        <Tab.Panels className="p-6">
                            <Tab.Panel>
                                <QuickAccountForm onSubmit={onCreated} />
                            </Tab.Panel>
                            <Tab.Panel>
                                <QuickVendorForm onSubmit={onCreated} />
                            </Tab.Panel>
                        </Tab.Panels>
                    </Tab.Group>

                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                    >
                        ×
                    </button>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
};

export const QuickAccountForm = ({ onSubmit }) => {
    const [accountData, setAccountData] = useState({
        name: '',
        accountNumber: ''
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(API.accounts.create(), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(accountData)
            });

            if (response.ok) {
                onSubmit();
                setAccountData({ name: '', accountNumber: '' });
            }
        } catch (error) {
            console.error('Error creating account:', error);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700">Name:</label>
                <input
                    type="text"
                    value={accountData.name}
                    onChange={(e) => setAccountData({ ...accountData, name: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700">Account Number:</label>
                <input
                    type="text"
                    value={accountData.accountNumber}
                    onChange={(e) => setAccountData({ ...accountData, accountNumber: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                />
            </div>
            <button
                type="submit"
                className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600"
            >
                Create Account
            </button>
        </form>
    );
};

export const QuickVendorForm = ({ onSubmit }) => {
    const [vendorData, setVendorData] = useState({
        name: '',
        address: '',
        email: '',
        phone: '',
        taxId: ''
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(API.vendors.create(), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(vendorData)
            });

            if (response.ok) {
                onSubmit();
                setVendorData({
                    name: '',
                    address: '',
                    email: '',
                    phone: '',
                    taxId: ''
                });
            }
        } catch (error) {
            console.error('Error creating vendor:', error);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700">Name:</label>
                <input
                    type="text"
                    value={vendorData.name}
                    onChange={(e) => setVendorData({ ...vendorData, name: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700">Address:</label>
                <input
                    type="text"
                    value={vendorData.address}
                    onChange={(e) => setVendorData({ ...vendorData, address: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700">Email:</label>
                <input
                    type="email"
                    value={vendorData.email}
                    onChange={(e) => setVendorData({ ...vendorData, email: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700">Phone:</label>
                <input
                    type="tel"
                    value={vendorData.phone}
                    onChange={(e) => setVendorData({ ...vendorData, phone: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700">Tax ID:</label>
                <input
                    type="text"
                    value={vendorData.taxId}
                    onChange={(e) => setVendorData({ ...vendorData, taxId: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                />
            </div>
            <button
                type="submit"
                className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600"
            >
                Create Vendor
            </button>
        </form>
    );
};